import { apiFetch, makeQueryKey, type CallApiParams } from "@web-monorepo/fetchers";
import { DEFAULT_STALE_TIME_MS, handleError } from "#/src/queryClient";

/**
 * Per-call options for a marketplace fetcher hook that renders its own failure state. Errors stay
 * on the query instead of throwing to the error boundary and go to the app's `handleError`, and
 * data keeps the app's freshness window instead of makeMemberQuery's `staleTime: Infinity`.
 * Spread it first so the hook's own options win. `ignoreExpected` is handleError's: it skips the
 * notification for errors the API marked expected.
 */
export const inlineErrorQueryOptions = ({ ignoreExpected = false }: { ignoreExpected?: boolean } = {}) => ({
  useErrorBoundary: false as const,
  staleTime: DEFAULT_STALE_TIME_MS,
  onError: handleError(ignoreExpected),
});

/** `apiFetch` for a react-query `queryFn`: resolves with the body and throws the error on failure. */
export async function fetchApiData<T>(params: Omit<CallApiParams, "responseType">): Promise<T> {
  const response = await apiFetch<T>(params);
  if (!response.success) {
    throw response.error;
  }
  return response.data;
}

type MemberFetcherConfig = { fetcherName: string; path: string; queryParams?: readonly string[] };
type MemberParams = Record<string, string | number | boolean | undefined>;

/**
 * The key and fetch a `makeMemberQuery` hook built from `config` uses for `params`, for
 * `queryClient.fetchQuery` / `useQueries` on the app queryClient. Pass the same config object to
 * `makeMemberQuery` so the URL has one source. Like fetchers, values are not encoded.
 */
export function memberQueryOptions<T>(config: MemberFetcherConfig, params: MemberParams) {
  const route = config.path;
  const pathPart = route.replace(/{(\w+)}/g, (_, name: string) => String(params[name]));
  const search = (config.queryParams ?? [])
    .flatMap((name) => (params[name] == null ? [] : [`${name}=${params[name]}`]))
    .join("&");
  return {
    queryKey: makeQueryKey({ fetcherName: config.fetcherName, params }),
    queryFn: () => fetchApiData<T>({ method: "GET", path: search ? `${pathPart}?${search}` : pathPart, route }),
  };
}
